/**
 * @fileoverview Mutation hook for Sales CRM commands that send an `Idempotency-Key`.
 * @description Keeps the command's key in a registry on the QueryClient (idempotency-keys.utils.ts),
 * not in the component, so it outlives the dialog that sent the command. The key is kept while the
 * outcome is unknown (network error, timeout, abort, 408, 5xx, SERVICE_UNAVAILABLE), so a retry of
 * the same command replays it even after the dialog was closed and reopened, and is dropped after a
 * success or a definite refusal (any other 4xx) so the next command gets a fresh one. A key belongs
 * to one action and its exact input; changed input takes a new key. The completion callback is an
 * option of the mutation itself rather than of each `mutate` call: TanStack Query skips the latter
 * once the component that called `mutate` has unmounted, which would lose the confirmation of a
 * command the server went on to apply. On success: the key is settled, every sales query refreshes,
 * then the callback runs.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { SALES_QUERY_KEY } from '@/features/sales/constants/prospects-query-keys.constants'
import { idempotencyKeysOf, idempotencyScope } from '@/features/sales/lib/idempotency-keys.utils'
import { isUnknownOutcomeError } from '@/features/sales/lib/idempotency.utils'
import { newIdempotencyKey } from '@/features/sales/services/prospects.service'

/** What a keyed command's mutation is made of. */
export interface IdempotentMutationOptions<TVariables, TData> {
  /** Which command this is, e.g. `withdraw-prospect` (it scopes the Idempotency-Key). */
  action: string
  /** Sends the command with the key it is given. */
  send: (variables: TVariables, idempotencyKey: string) => Promise<TData>
  /**
   * What identifies the command the person meant, for scoping its key. Leave out the version the
   * command is sent against (an ETag, an approval version): it can change between an attempt and
   * its retry (a refetch on focus), and the retry must still replay the same key. Defaults to the
   * whole input.
   */
  scopeOf?: ((variables: TVariables) => unknown) | undefined
  /** Called with the result after a success, even when the dialog has unmounted since. */
  onSuccess?: ((result: TData, variables: TVariables) => void) | undefined
}

/**
 * @description Wraps a keyed command in a mutation that owns the key's lifecycle.
 * @param {IdempotentMutationOptions<TVariables, TData>} options - The command and what follows it.
 * @param {string} options.action - Which command this is.
 * @param {(variables: TVariables, idempotencyKey: string) => Promise<TData>} options.send - Sends
 * the command with the key it is given.
 * @param {(variables: TVariables) => unknown} [options.scopeOf] - What identifies the command for
 * its key, without the version it is sent against. Defaults to the whole input.
 * @param {(result: TData, variables: TVariables) => void} [options.onSuccess] - Called after a
 * success.
 * @returns {ReturnType<typeof useMutation>} The mutation (mutate with the command's input).
 */
export function useIdempotentMutation<TVariables, TData>({
  action,
  send,
  scopeOf,
  onSuccess,
}: IdempotentMutationOptions<TVariables, TData>) {
  const queryClient = useQueryClient()
  const keys = idempotencyKeysOf(queryClient)

  /**
   * @description The scope this command's key is kept under.
   * @param {TVariables} variables - The command's input.
   * @returns {string} The scope: the action and what identifies the command.
   */
  const scopeFor = (variables: TVariables) =>
    idempotencyScope(action, scopeOf ? scopeOf(variables) : variables)

  return useMutation<TData, Error, TVariables>({
    /**
     * @description Sends the command under the key kept for this action and input.
     * @param {TVariables} variables - The command's input.
     * @returns {Promise<TData>} The command's result.
     */
    mutationFn: (variables) => {
      const scope = scopeFor(variables)
      const key = keys.get(scope) ?? newIdempotencyKey()
      keys.set(scope, key)
      return send(variables, key)
    },
    /**
     * @description Forgets the key (the same command later is a new one), refreshes every sales
     * query, then reports the result.
     * @param {TData} result - The command's result.
     * @param {TVariables} variables - The command's input.
     */
    onSuccess: (result, variables) => {
      keys.delete(scopeFor(variables))
      void queryClient.invalidateQueries({ queryKey: SALES_QUERY_KEY })
      onSuccess?.(result, variables)
    },
    /**
     * @description Keeps the key when the outcome is unknown, so a retry is a replay; drops it after
     * a definite refusal.
     * @param {Error} error - The failed attempt's error.
     * @param {TVariables} variables - The command's input.
     */
    onError: (error, variables) => {
      if (!isUnknownOutcomeError(error)) keys.delete(scopeFor(variables))
    },
  })
}
